import React, { useEffect, useState } from "react";
import "./Auth.css";
import loginVec from "../../Assets/Images/loginVec.webp";
import brandLogo from "../../Assets/Images/brandLogo.png";
import { MdOutlineRemoveRedEye, MdOutlineVisibilityOff } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { postApiCaller } from "../../Libs/apiCaller";
import { toast } from "react-toastify";
import { useCookies } from "react-cookie";
import { logedInUser } from "../../Redux/slices/AuthSlice";
import { useDispatch, useSelector } from "react-redux";

const SignIn = () => {
  const { fcmToken } = useSelector((state) => state.authUser);
  const [showPassword, setShowPassword] = useState(false);
  const [isRememberMe, setIsRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setCookie] = useCookies(["accessToken"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("isPassChanged")) {
      localStorage.removeItem("isPassChanged");
    } else if (localStorage.getItem("isSignUp")) {
      localStorage.removeItem("isSignUp");
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Username or Email is required")
        .test(
          "is-valid-email-or-username",
          "Invalid email or username",
          function (value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const usernameRegex = /^[a-zA-Z0-9_.-]{3,}$/;
            return emailRegex.test(value) || usernameRegex.test(value);
          }
        ),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const payload = {
        emailOrUserName: values.email,
        password: values.password,
        fcmToken: fcmToken,
      };
      const endPoint = "auth/login_with_password";
      const response = await postApiCaller(endPoint, payload);
      if (response?.status === 200) {
        toast.success(response.data.message);
        dispatch(logedInUser(response.data.user));
        setCookie("accessToken", response.data.accessToken, {
          path: "/",
          secure: true,
          httpOnly: false,
          sameSite: "Strict",
          maxAge: 2147483647, // Maximum value for 32-bit integer
        });
        localStorage.setItem("userId", response.data.user._id);
        navigate(
          response.data.user.role == "Admin" ? "/admin/dashboard" : "/home",
          { state: response.data.user }
        );
      } else {
        toast.error(response?.response?.data?.message || response.message);
      }
      setIsLoading(false);
    },
  });

  return (
    <section
      className="bg-login p-6 md:p-4 pb-16 sm:pb-0 min-h-screen flex items-center justify-center
overflow-x-hidden"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 items-stretch w-full max-w-8xl mx-auto gap-0 md:gap-10">
        {/* Right Side - Image and Overlay */}
        <div
          className="hidden md:grid relative grid-cols-1 h-full min-h-[calc(100vh-40px)] bg-cover bg-top-center rounded-xl"
          style={{ backgroundImage: `url(${loginVec})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-20 rounded-xl"></div>

          <div className="absolute top-0 p-8 text-white">
            <img src={brandLogo} alt="Brand Logo" className="w-20 h-20 mb-4 drop-shadow-lg" />
          </div>

          <div className="absolute bottom-4 mx-4 w-[calc(100%-2rem)] text-left rounded-lg text-white p-3 backdrop-blur-xs border border-white/30 bg-white/10">
            <p className="capitalize text-md">
              We believe communication should be simple, fast and private.
              Whether you're chatting with friends, connecting with family or
              collaborating with your team, our platform ensures seamless
              messaging and end-to-end security.
            </p>
          </div>
        </div>

        {/* Left Side - Form */}
        <div className="flex items-center justify-center px-4 md:px-0">
          <div className="w-full max-w-lg">
            <h2 className="text-3xl leading-[50px] font-bold text-gray-900 mb-4">
              Welcome Back! <br /> Glad To See You,Again!
            </h2>
            <p className="text-md text-gray-500 mb-6">
              Please enter your credentials to access your account.
            </p>

            <form onSubmit={formik.handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm mb-2">
                  Username or Email Address
                </label>
                <input
                  type="text"
                  name="email"
                  {...formik.getFieldProps("email")}
                  className={`w-full bg-white px-4 py-3.5 rounded-lg focus:outline-none focus:ring-2 ${formik.touched.email && formik.errors.email
                      ? "focus:ring-red-500 border-red-500"
                      : "focus:ring-purple-600"
                    }`}
                  placeholder="Enter Username or Email Address"
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 text-sm mt-1 errorMsg">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              <div className="mb-4 relative">
                <label className="block text-gray-700 text-sm mb-2">
                  Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  {...formik.getFieldProps("password")}
                  className={`w-full bg-white px-4 py-3.5 rounded-lg focus:outline-none focus:ring-2 pr-10 ${formik.touched.password && formik.errors.password
                      ? "focus:ring-red-500 border-red-500"
                      : "focus:ring-purple-600"
                    }`}
                  placeholder="Enter Password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <MdOutlineRemoveRedEye size={22} />
                  ) : (
                    <MdOutlineVisibilityOff size={22} />
                  )}
                </button>
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-500 text-sm mt-1 errorMsg">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              <div className="flex justify-end items-center mb-6">
                {/* <label className="flex items-center text-sm text-gray-600">
                  <input
                    type="checkbox"
                    className="mr-2 checkbox-xs checkbox rounded-sm border-[#aaa] checkbox-primary"
                    checked={isRememberMe}
                    onChange={(e) => setIsRememberMe(e.target.checked)}
                  />
                  Remember Me
                </label> */}
                <Link
                  to="/forgot-password"
                  className="text-[var(--primary-color)] text-sm hover:underline w-fit"
                >
                  Forgot Password?
                </Link>
              </div>

              <button
                type="submit"
                className="w-full bg-[var(--primary-color)] text-white py-3 rounded-full text-lg font-medium hover:bg-purple-500 transition"
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Login"}
              </button>
            </form>

            <div className="flex items-center my-4">
              <div className="w-full border-t border-[#A3AEBE] border-dashed"></div>
              <span className="px-3 text-gray-500">Or</span>
              <div className="w-full border-t border-[#A3AEBE] border-dashed"></div>
            </div>

            <Link
              to="/signin-via-number"
              className="text-center flex mx-auto justify-center text-purple-600 font-semibold hover:underline w-fit"
            >
              Login With Phone Number
            </Link>

            <p className="text-center text-gray-600 text-md mt-5">
              Don't have an account?{" "}
              <Link
                to="/sign-up"
                className="text-purple-600 font-semibold hover:underline w-fit"
              >
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignIn;
